import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * QA-80 (ID80) 상품정보고시 템플릿 — BE `/admin/v1/notice-spec-templates` CRUD 계약 소비.
 *
 * items 는 라벨→값 순서 보존 맵(products.notice_spec 과 동일 shape). 상품 폼은 선택한 템플릿의
 * items 를 noticeSpec 으로 prefill 한다(약결합 — 템플릿 수정/삭제가 기존 상품 미영향).
 */
export interface NoticeSpecTemplate {
  templateId: string;
  name: string;
  /** 고시 항목 라벨→값(ko-KR 정본). 순서 보존(LinkedHashMap 직렬화). */
  items: Record<string, string>;
  /**
   * Task#6 — locale 별 고시 항목(en-US/ja-JP/zh-CN, 저장된 locale 만 포함).
   * 상품 폼의 해당 locale 탭이 prefill 하고, 행이 없는 locale 은 ko items 로 폴백한다.
   * (NoticeSpecTemplateView.translations — 구버전 응답엔 없을 수 있어 optional.)
   */
  translations?: Record<string, Record<string, string>> | null;
  createdAt: string;
  updatedAt: string;
}

export interface NoticeSpecTemplatePayload {
  name: string;
  items: Record<string, string>;
  /**
   * Task#6 — BE 시맨틱(NoticeSpecTemplateRequest): 필드 생략(null)=미변경, locale 값 빈맵=해당
   * locale 삭제, 그 외=해당 locale 전체 교체. 모달은 3개 locale 키를 항상 명시 전송(결정적 교체).
   */
  translations?: Record<string, Record<string, string>>;
}

const BASE = '/admin/v1/notice-spec-templates';

/** be-catalog 계약: admin write 는 Idempotency-Key 헤더 필요. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

interface ListEnvelope {
  success: boolean;
  data?: NoticeSpecTemplate[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: NoticeSpecTemplate | null;
}

export const noticeSpecTemplatesQueryKey = ['admin', 'notice-spec-templates'] as const;

const fetchTemplates = async (): Promise<NoticeSpecTemplate[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`notice-spec templates fetch failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

export const useNoticeSpecTemplatesQuery = () =>
  useQuery<NoticeSpecTemplate[]>({
    queryKey: noticeSpecTemplatesQueryKey,
    queryFn: fetchTemplates,
    staleTime: 60 * 1000,
  });

export const useNoticeSpecTemplateMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: noticeSpecTemplatesQueryKey });

  const create = useMutation({
    mutationFn: async (payload: NoticeSpecTemplatePayload): Promise<NoticeSpecTemplate> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: writeHeaders('POST', BASE, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`notice-spec template create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: NoticeSpecTemplatePayload;
    }): Promise<NoticeSpecTemplate> => {
      const path = `${BASE}/${templateId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`notice-spec template update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (templateId: string): Promise<void> => {
      const path = `${BASE}/${templateId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: writeHeaders('DELETE', path, ''),
      });
      if (!res.ok) throw new Error(`notice-spec template delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
